import { MouseEvent, RefObject, useCallback, useEffect, useRef } from 'react';
import useStore from '../../../../infrastructure/store/useStore';
import useMaskChanges from '../../hooks/useMaskChanges';
import useSetRef from '../../hooks/useSetRef';
import useTopView from '../../MaskSelector/hooks/useTopView';
import ImageEditorConfig from '../constants/ImageEditor.config';
import centerCoords from '../helpers/centerCoords';
import ICoordinates from '../types/ICoordinates';
import IToolsEnum from '../types/IToolsEnum';
import useBoundaries from './useBoundaries';

const useCanvasDraw = (
  id: string,
  canvasRef: RefObject<HTMLCanvasElement> | null,
  color: string,
) => {
  const [drawingRef, setDrawing] = useSetRef(false);
  const { boundingRef, clearBounding, updateBounding } = useBoundaries();
  const { pushChanges, checkIfWasErased, getErasedPixelsForRows } = useMaskChanges();
  const { copyToTopView } = useTopView();
  const [lastCoordsRef, setLastCoords] = useSetRef<ICoordinates | null>(null);
  const [
    masks,
    maskRefs,
    selectedMaskIndex,
    selectedTool,
    selectedImage,
    brushSize,
    maskImageDataStack,
    limitReached,
    shouldClearCoords,
    setBrushSize,
    updateErasedCoordinates,
  ] = useStore((state) => [
    state.masks,
    state.maskRefs,
    state.selectedMaskIndex,
    state.selectedTool,
    state.selectedImage,
    state.brushSize,
    state.maskImageDataStack,
    state.limitReached,
    state.shouldClearCoords,
    state.setBrushSize,
    state.updateErasedCoordinates,
  ]);
  const [currentTool, setCurrentTool] = useSetRef<IToolsEnum>(selectedTool);

  //////// Added on 2025-11-17
  const needsRenderRef = useRef(false);
  ////////

  useEffect(() => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    if (selectedImage?.isVIPImage) {
      ctx.lineCap = 'square';
      ctx.lineJoin = 'miter';
      ctx.imageSmoothingQuality = 'high';
      ctx.lineWidth = ImageEditorConfig.VIP_ERASER_SIZE;
    } else {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.imageSmoothingQuality = 'high';
      ctx.lineWidth = brushSize;
      ctx.strokeStyle = color;
    }
  }, [
    canvasRef?.current,
    selectedImage?.isVIPImage,
    brushSize,
    color,
  ]);

  useEffect(() => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      if (!selectedImage?.isVIPImage) {
        ctx.lineWidth = brushSize;
      }
      ctx.stroke();
    }
  }, [brushSize, selectedImage?.isVIPImage]);

  const clearCoords = () => {
    clearBounding();
  };

  useEffect(() => {
    if (shouldClearCoords) {
      clearCoords();
    }
  }, [shouldClearCoords]);

  const centerCoordsForVIP = useCallback(
    (coords: ICoordinates) => {
      const canvas = canvasRef?.current;
      if (!selectedImage?.isVIPImage || !canvas) return coords;
      return centerCoords(coords, canvas.height);
    },
    [selectedImage],
  );

  const drawOnCenterCoordinates = useCallback(
    (coords: ICoordinates) => {
      const canvas = canvasRef?.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      if (lastCoordsRef.current === null) {
        const { x, y } = centerCoords(coords, canvas.height);

        setLastCoords({ x, y });
        updateBounding({ x, y });

        // request render
        needsRenderRef.current = true;
        return;
      }

      // store next coordinate
      setLastCoords(coords);

      // request render
      needsRenderRef.current = true;

      updateBounding(coords);
    },
    [updateBounding],
  );

  const copyToMask = (
    source: HTMLCanvasElement,
    target: HTMLCanvasElement,
    deleteArea: boolean = false,
  ) => {
    if (!source) return;
    const targetCanvas = target;
    if (targetCanvas) {
      const targetCtx = targetCanvas.getContext('2d');
      if (targetCtx) {
        targetCtx.globalCompositeOperation = deleteArea ? 'destination-out' : 'source-over';
        targetCtx.drawImage(source, 0, 0);
        targetCtx.globalCompositeOperation = 'source-over';
      }
    }
  };

  const clearMasks = () => {
    const source = maskRefs[selectedMaskIndex].current;
    if (!source) return;
    maskRefs.forEach((maskRef, index) => {
      if (maskRef && maskRef.current && index !== selectedMaskIndex) {
        copyToMask(source, maskRef.current, true);
      }
    });
  };

  const onToolChange = useCallback(() => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    clearCoords();
    if (selectedTool !== currentTool.current) {
      setBrushSize(
        [IToolsEnum.TOOL_PENCIL, IToolsEnum.TOOL_SWORD, IToolsEnum.TOOL_SCISSORS].includes(
          selectedTool,
        )
          ? ImageEditorConfig.DEFAULT_PENCIL_SIZE
          : ImageEditorConfig.DEFAULT_BRUSH_SIZE,
      );
    }
    setCurrentTool(selectedTool);
    if (currentTool.current === IToolsEnum.TOOL_ERASER) {
      ctx.globalCompositeOperation = 'destination-out';
      return;
    }
    ctx.globalCompositeOperation = 'source-over';
  }, [selectedTool]);

  const checkForDiscard = useCallback(() => {
    if (maskImageDataStack.length === 0 || limitReached) {
      clearCoords();
    }
  }, [maskImageDataStack, limitReached]);

  useEffect(checkForDiscard, [checkForDiscard]);
  useEffect(onToolChange, [onToolChange]);

  const onMouseDown = (e: MouseEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;

    if (selectedMaskIndex !== null && !masks[selectedMaskIndex].visible) return;

    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    if (currentTool.current === IToolsEnum.TOOL_HAND) return;

    const coords = centerCoordsForVIP({
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    });

    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);

    // store first point
    setLastCoords(coords);

    // request drawing
    needsRenderRef.current = true;

    // mark as drawing
    setDrawing(true);

    updateBounding(coords);
  };

  const onMouseUp = () => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas || !drawingRef.current) return;
    ctx.closePath();
    setDrawing(false);
    setLastCoords(null);
    if (currentTool.current === IToolsEnum.TOOL_HAND) {
      return;
    }
    if (currentTool.current === IToolsEnum.TOOL_BRUSH) {
      clearMasks();
    }
    if (currentTool.current === IToolsEnum.TOOL_ERASER) {
      if (selectedImage?.isVIPImage) {
        const { foundErased, coords } = getErasedPixelsForRows(
          id,
          boundingRef.current,
          ctx.lineWidth,
        );
        if (!foundErased) return;
        updateErasedCoordinates(coords);
        copyToTopView(canvasRef?.current);
      }

      if (!checkIfWasErased(id, boundingRef.current, ctx.lineWidth)) return;
    }
    pushChanges();
  };

  const onMouseMove = (e: MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    if (!drawingRef.current || !ctx) return;

    const coords = { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };

    if (selectedImage?.isVIPImage) {
      drawOnCenterCoordinates(coords);
      return;
    }

    lastCoordsRef.current = coords;
    needsRenderRef.current = true;

    updateBounding(coords);
  };

  const onMouseEnter = (e: MouseEvent<HTMLCanvasElement>) => {
    if (e.nativeEvent.buttons !== 1) {
      setDrawing(false);
      setLastCoords(null);
    }
  };

  useEffect(() => {
    let frameId: number;

    const renderLoop = () => {
      if (needsRenderRef.current && drawingRef.current) {
        const canvas = canvasRef?.current;
        const ctx = canvas?.getContext('2d');

        if (canvas && ctx && lastCoordsRef.current) {
          ctx.lineTo(lastCoordsRef.current.x, lastCoordsRef.current.y);
          ctx.stroke();
        }

        needsRenderRef.current = false;
      }

      frameId = requestAnimationFrame(renderLoop);
    };

    frameId = requestAnimationFrame(renderLoop);

    return () => cancelAnimationFrame(frameId);
  }, [canvasRef]);

  return {
    setDrawing,
    onMouseDown,
    onMouseUp,
    onMouseMove,
    onMouseEnter,
    onContextMenu: (e: MouseEvent<HTMLCanvasElement>) => e.preventDefault(),
  };
};

export default useCanvasDraw;
